import React, { useState, useEffect } from 'react';
import {
  Container,
  Box,
  Typography,
  Paper,
  IconButton,
  TextField,
  Button,
  FormControl,
  FormControlLabel,
  Checkbox,
  FormGroup,
  Select,
  MenuItem,
  InputLabel,
  Alert,
  CircularProgress,
  Divider,
  Grid,
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Send as SendIcon,
  People as PeopleIcon,
  Email as EmailIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import apiClient from '../../config/axios';
import getConfig from '../../config/app-config';
import ModalConfirm from '../../components/ModalConfirm';
import ModalResult from '../../components/ModalResult';

const Communications = () => {
  const navigate = useNavigate();
  const config = getConfig();
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [courses, setCourses] = useState([]);
  const [recipientsPreview, setRecipientsPreview] = useState(null);
  const [showRecipientsDialog, setShowRecipientsDialog] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [recipientsCount, setRecipientsCount] = useState(0);
  const [showResultModal, setShowResultModal] = useState(false);
  const [resultData, setResultData] = useState({ message: '', severity: 'info', title: '' });

  // Form state
  const [formData, setFormData] = useState({
    subject: '',
    body: '',
    criteria: {
      allUsers: false,
      activeMembers: false,
      allTeachers: false,
      courseParticipants: false,
      courseId: '',
      includeTeacher: false
    },
    sendCopyToSender: false
  });

  useEffect(() => {
    fetchCourses();
  }, []);

  const fetchCourses = async () => {
    try {
      const response = await apiClient.get('/api/courses');
      setCourses(response.data);
    } catch (error) {
      console.error('Error fetching courses:', error);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleCriteriaChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      criteria: {
        ...prev.criteria,
        [field]: value
      }
    }));
  };

  const getRecipientsPreview = async () => {
    setLoading(true);
    
    try {
      const response = await apiClient.post('/api/user/communication/recipients', {
        criteria: formData.criteria
      });
      
      setRecipientsPreview(response.data);
      setShowRecipientsDialog(true);
    } catch (error) {
      setResultData({
        message: error.response?.data?.message || 'Errore nel recupero dei destinatari',
        severity: 'error',
        title: 'Errore'
      });
      setShowResultModal(true);
    } finally {
      setLoading(false);
    }
  };

  const checkRecipientsAndConfirm = async () => {
    if (!formData.subject.trim() || !formData.body.trim()) {
      setResultData({
        message: 'Titolo e corpo della comunicazione sono obbligatori',
        severity: 'error',
        title: 'Errore di Validazione'
      });
      setShowResultModal(true);
      return;
    }

    const hasCriteria = Object.values(formData.criteria).some(value => 
      typeof value === 'boolean' ? value : (typeof value === 'string' && value)
    );

    if (!hasCriteria) {
      setResultData({
        message: 'Seleziona almeno un criterio per i destinatari',
        severity: 'error',
        title: 'Errore di Validazione'
      });
      setShowResultModal(true);
      return;
    }

    setLoading(true);

    try {
      const response = await apiClient.post('/api/user/communication/recipients', {
        criteria: formData.criteria
      });
      
      setRecipientsCount(response.data.total);
      setShowConfirmDialog(true);
    } catch (error) {
      setResultData({
        message: error.response?.data?.message || 'Errore nel recupero dei destinatari',
        severity: 'error',
        title: 'Errore'
      });
      setShowResultModal(true);
    } finally {
      setLoading(false);
    }
  };

  const sendCommunication = async () => {
    setSending(true);
    setShowConfirmDialog(false);

    try {
      const response = await apiClient.post('/api/user/communication/send', formData);
      
      setResultData({
        message: `Comunicazione inviata con successo!\n\nInviate: ${response.data.sent} email\nFallite: ${response.data.failed} email\nDestinatari totali: ${response.data.recipients}`,
        severity: 'success',
        title: 'Comunicazione Inviata'
      });
      setShowResultModal(true);
      
      // Reset form
      setFormData({
        subject: '',
        body: '',
        criteria: {
          allUsers: false,
          activeMembers: false,
          allTeachers: false,
          courseParticipants: false,
          courseId: '',
          includeTeacher: false
        },
        sendCopyToSender: false
      });
    } catch (error) {
      setResultData({
        message: error.response?.data?.message || 'Errore nell\'invio della comunicazione',
        severity: 'error',
        title: 'Errore di Invio'
      });
      setShowResultModal(true);
    } finally {
      setSending(false);
    }
  };

  const getSelectedCriteriaText = () => {
    const criteria = [];
    if (formData.criteria.allUsers) criteria.push('Tutti gli utenti');
    if (formData.criteria.activeMembers) criteria.push('Tesserati attivi');
    if (formData.criteria.allTeachers) criteria.push('Tutti i docenti');
    if (formData.criteria.courseParticipants) {
      const course = courses.find(c => c._id === formData.criteria.courseId);
      criteria.push(`Partecipanti al corso: ${course?.name || 'Corso selezionato'}`);
    }
    return criteria.length > 0 ? criteria.join(', ') : 'Nessun criterio selezionato';
  };

  return (
    <Container maxWidth="xl">
      <Box sx={{ py: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <IconButton onClick={() => navigate('/admin')} sx={{ mr: 2 }}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h4">
            Comunicazioni
          </Typography>
        </Box>

        {/* Communication Banner */}
        <Box sx={{ mb: 4 }}>
          <Paper 
            elevation={2}
            sx={{
              position: 'relative',
              overflow: 'hidden',
              borderRadius: 2,
              height: 200,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: `url(${config.COMMUNICATION_IMAGE})`,
              backgroundSize: { xs: 'cover', md: '100%' },
              backgroundPosition: { xs: '-400px center', md: 'center center' },
              backgroundRepeat: 'no-repeat'
            }}
          >
            
          </Paper>
        </Box>

        <Grid container spacing={3}>
          {/* Form Section */}
          <Grid size={{ xs: 12, md: 8 }}>
            <Paper sx={{ p: 3 }}>
              <Alert severity="warning" sx={{ mb: 3 }}>
                <Typography variant="body2">
                  <strong>Attenzione:</strong> Con il tipo di abbonamento in corso è possibile inviare qualche centinaio di email al giorno, si prega di non abusare di questa funzionalità.
                </Typography>
              </Alert>
              
              <Typography variant="h6" gutterBottom>
                Nuova Comunicazione
              </Typography>
              
              <Box sx={{ mb: 3 }}>
                <TextField
                  fullWidth
                  label="Titolo della comunicazione"
                  value={formData.subject}
                  onChange={(e) => handleInputChange('subject', e.target.value)}
                  sx={{ mb: 2 }}
                />
                
                <TextField
                  fullWidth
                  label="Corpo della comunicazione"
                  multiline
                  rows={8}
                  value={formData.body}
                  onChange={(e) => handleInputChange('body', e.target.value)}
                  placeholder="Scrivi qui il contenuto della comunicazione..."
                />
              </Box>

              <Divider sx={{ my: 3 }} />

              <Typography variant="h6" gutterBottom>
                Destinatari
              </Typography>

              <FormGroup>
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={formData.criteria.allUsers}
                          onChange={(e) => handleCriteriaChange('allUsers', e.target.checked)}
                        />
                      }
                      label="Tutti gli account"
                    />
                  </Grid>
                  
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={formData.criteria.activeMembers}
                          onChange={(e) => handleCriteriaChange('activeMembers', e.target.checked)}
                        />
                      }
                      label="Tesserati attivi"
                    />
                  </Grid>
                  
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={formData.criteria.allTeachers}
                          onChange={(e) => handleCriteriaChange('allTeachers', e.target.checked)}
                        />
                      }
                      label="Tutti i docenti"
                    />
                  </Grid>
                  
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={formData.criteria.courseParticipants}
                          onChange={(e) => handleCriteriaChange('courseParticipants', e.target.checked)}
                        />
                      }
                      label="Partecipanti a un corso"
                    />
                  </Grid>
                </Grid>

                {formData.criteria.courseParticipants && (
                  <Box sx={{ mt: 2 }}>
                    <FormControl fullWidth sx={{ mb: 2 }}>
                      <InputLabel>Corso</InputLabel>
                      <Select
                        value={formData.criteria.courseId}
                        onChange={(e) => handleCriteriaChange('courseId', e.target.value)}
                        label="Corso"
                      >
                        {courses.map((course) => (
                          <MenuItem key={course._id} value={course._id}>
                            {course.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={formData.criteria.includeTeacher}
                          onChange={(e) => handleCriteriaChange('includeTeacher', e.target.checked)}
                        />
                      }
                      label="Includi docente del corso"
                    />
                  </Box>
                )}
              </FormGroup>

              <Divider sx={{ my: 3 }} />

              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.sendCopyToSender}
                    onChange={(e) => handleInputChange('sendCopyToSender', e.target.checked)}
                  />
                }
                label="Invia una copia a me stesso"
              />

              <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
                <Button
                  variant="outlined"
                  onClick={getRecipientsPreview}
                  disabled={loading}
                  startIcon={loading ? <CircularProgress size={20} /> : <PeopleIcon />}
                >
                  Anteprima Destinatari
                </Button>
                
                <Button
                  variant="contained"
                  onClick={checkRecipientsAndConfirm}
                  disabled={loading || sending}
                  startIcon={loading || sending ? <CircularProgress size={20} /> : <SendIcon />}
                >
                  {loading ? 'Controllo destinatari...' : sending ? 'Invio in corso...' : 'Invia Comunicazione'}
                </Button>
              </Box>
            </Paper>
          </Grid>

          {/* Preview Section */}
          <Grid size={{ xs: 12, md: 4 }}>
            <Paper sx={{ p: 3, height: 'fit-content', position: 'sticky', top: 24 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Anteprima
                </Typography>
              </Box>
              
              <Card sx={{ mb: 3, border: '1px solid', borderColor: 'divider' }}>
                <CardContent sx={{ p: 2.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                    <PeopleIcon sx={{ mr: 1, color: 'primary.main', fontSize: 20 }} />
                    <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 500 }}>
                      Destinatari selezionati
                    </Typography>
                  </Box>
                  <Typography variant="body2" sx={{ 
                    color: 'text.primary',
                    backgroundColor: 'action.hover',
                    p: 1.5,
                    borderRadius: 1,
                    border: '1px solid',
                    borderColor: 'divider'
                  }}>
                    {getSelectedCriteriaText()}
                  </Typography>
                </CardContent>
              </Card>

              {formData.subject && (
                <Card sx={{ mb: 3, border: '1px solid', borderColor: 'divider' }}>
                  <CardContent sx={{ p: 2.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                      <EmailIcon sx={{ mr: 1, color: 'primary.main', fontSize: 20 }} />
                      <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 500 }}>
                        Oggetto
                      </Typography>
                    </Box>
                    <Typography variant="body2" sx={{ 
                      color: 'text.primary',
                      backgroundColor: 'action.hover',
                      p: 1.5,
                      borderRadius: 1,
                      border: '1px solid',
                      borderColor: 'divider',
                      fontWeight: 500
                    }}>
                      {formData.subject}
                    </Typography>
                  </CardContent>
                </Card>
              )}

              {formData.body && (
                <Card sx={{ border: '1px solid', borderColor: 'divider' }}>
                  <CardContent sx={{ p: 2.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                      <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 500 }}>
                        Anteprima corpo
                      </Typography>
                    </Box>
                    <Box sx={{ 
                      backgroundColor: 'action.hover',
                      p: 1.5,
                      borderRadius: 1,
                      border: '1px solid',
                      borderColor: 'divider',
                      maxHeight: 200,
                      overflow: 'hidden',
                      position: 'relative'
                    }}>
                      <Typography variant="body2" sx={{ 
                        color: 'text.primary',
                        lineHeight: 1.6,
                        whiteSpace: 'pre-wrap'
                      }}>
                        {formData.body}
                      </Typography>
                      {formData.body.length > 300 && (
                        <Box sx={{
                          position: 'absolute',
                          bottom: 0,
                          left: 0,
                          right: 0,
                          height: 40,
                          background: 'linear-gradient(transparent, rgba(255,255,255,0.9))',
                          pointerEvents: 'none'
                        }} />
                      )}
                    </Box>
                    {formData.body.length > 300 && (
                      <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                        Testo troncato - {formData.body.length} caratteri
                      </Typography>
                    )}
                  </CardContent>
                </Card>
              )}

              {!formData.subject && !formData.body && (
                <Box sx={{ 
                  textAlign: 'center', 
                  py: 4,
                  color: 'text.secondary'
                }}>
                  <Typography variant="body2">
                    Inizia a scrivere per vedere l'anteprima
                  </Typography>
                </Box>
              )}
            </Paper>
          </Grid>
        </Grid>

        {/* Recipients Preview Dialog */}
        <Dialog
          open={showRecipientsDialog}
          onClose={() => setShowRecipientsDialog(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <EmailIcon sx={{ mr: 1 }} />
              Anteprima Destinatari ({recipientsPreview?.total || 0})
            </Box>
          </DialogTitle>
          <DialogContent>
            {recipientsPreview?.preview?.length > 0 ? (
              <>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Primi 10 destinatari (totale: {recipientsPreview.total})
                </Typography>
                <List>
                  {recipientsPreview.preview.map((recipient, index) => (
                    <ListItem key={index}>
                      <ListItemAvatar>
                        <Avatar>
                          <EmailIcon />
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={recipient.name}
                        secondary={recipient.email}
                      />
                    </ListItem>
                  ))}
                </List>
              </>
            ) : (
              <Typography variant="body1" color="text.secondary">
                Nessun destinatario trovato per i criteri selezionati.
              </Typography>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowRecipientsDialog(false)}>
              Chiudi
            </Button>
          </DialogActions>
        </Dialog>

        {/* Confirmation Dialog */}
        <ModalConfirm
          open={showConfirmDialog}
          title="Conferma Invio Comunicazione"
          message={`Stai per inviare questa comunicazione a ${recipientsCount} destinatari, confermi?`}
          onConfirm={sendCommunication}
          onCancel={() => setShowConfirmDialog(false)}
        />

        {/* Result Modal */}
        <ModalResult
          open={showResultModal}
          onClose={() => setShowResultModal(false)}
          message={resultData.message}
          severity={resultData.severity}
          title={resultData.title}
        />
      </Box>
    </Container>
  );
};

export default Communications; 